import { EVENT_DELAY } from '@src/config/constants'
import { initializePinterestTag } from '@src/quince-site-common/src/trackers/PinterestInitializer'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { initializeScript } from '@src/telemetry/integrations/provider-utils/initialize-script'
import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'
import { getStorefrontIdProperty } from '@src/utils/b2b'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'

import { TELEMETRY_DOMAINS } from '../telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'

import { getSHA256Hashed } from './provider-utils/data-layer-pixel-utils'

export const PinterestTag: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.PINTEREST_TAG,
    featureGateName: 'ssr_telemetry_pinterest_tag',
  },

  initialize: async () => {
    if (!window.pintrk) {
      window.pintrk = function (...args: unknown[]) {
        if (window.pintrk) {
          if (!window.pintrk.queue) {
            window.pintrk.queue = []
          } else {
            window.pintrk.queue.push(args)
          }
        }
      }
      window.pintrk.queue = []
      window.pintrk.version = '3.0'
    }

    await initializeScript({
      key: 'pinterest-tag',
      domain: TELEMETRY_DOMAINS.PINTEREST_TAG,
      path: '/ct/core.js',
      injectAt: 'beforeFirstScript',
      onLoad: initializePinterestTag,
    })
  },

  events: {
    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: async properties => {
      const lbUserId = getlbUserId()?.trim() ?? ''
      const lbUserIdSHA256 = (await getSHA256Hashed(lbUserId)) ?? ''
      executeFireAndForgotTask(() => {
        if (window.pintrk) {
          window.pintrk('track', 'addtocart', {
            currency: 'USD',
            event_id: properties.eventId,
            external_id: lbUserIdSHA256,
            line_items: [
              {
                product_id: properties.productId,
                product_variant_id: properties.variantId,
                product_name:
                  properties.productTitle && properties.variantTitle
                    ? properties.productTitle + ' - ' + properties.variantTitle
                    : '',
                product_price: properties.price,
                product_quantity: properties.quantity || 1,
              },
            ],
            ...getStorefrontIdProperty(), //storefront_id is used to identify the b2b self serve flow
          })
        }
      }, EVENT_DELAY)
    },

    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW]: async properties => {
      const localisationData = getLocalisationDataFromStorage()
      const lbUserId = getlbUserId()?.trim() ?? ''
      const lbUserIdSHA256 = (await getSHA256Hashed(lbUserId)) ?? ''
      executeFireAndForgotTask(() => {
        if (window.pintrk) {
          window.pintrk('track', 'pagevisit', {
            currency: localisationData?.currency,
            country: localisationData?.country,
            event_id: properties.eventId,
            external_id: lbUserIdSHA256,
            line_items: [
              {
                product_id: properties.productId,
                product_variant_id: properties.variantId,
                product_name:
                  properties.productTitle && properties.variantTitle
                    ? properties.productTitle + ' - ' + properties.variantTitle
                    : '',
                product_price: properties.price,
                product_quantity: 1,
              },
            ],
            ...getStorefrontIdProperty(), //storefront_id is used to identify the b2b self serve flow
          })
        }
      }, EVENT_DELAY)
    },

    [TELEMETRY_EVENT_NAMES.SEARCH]: async properties => {
      const localisationData = getLocalisationDataFromStorage()
      const lbUserId = getlbUserId()?.trim() ?? ''
      const lbUserIdSHA256 = (await getSHA256Hashed(lbUserId)) ?? ''
      executeFireAndForgotTask(() => {
        if (window.pintrk) {
          window.pintrk('track', 'search', {
            currency: localisationData?.currency,
            country: localisationData?.country,
            event_id: properties.eventId,
            external_id: lbUserIdSHA256,
            search_query: properties.searchQuery,
            ...getStorefrontIdProperty(), //storefront_id is used to identify the b2b self serve flow
          })
        }
      }, EVENT_DELAY)
    },

    [TELEMETRY_EVENT_NAMES.COLLECTION_VIEW]: async properties => {
      const localisationData = getLocalisationDataFromStorage()
      const lbUserId = getlbUserId()?.trim() ?? ''
      const lbUserIdSHA256 = (await getSHA256Hashed(lbUserId)) ?? ''
      executeFireAndForgotTask(() => {
        if (window.pintrk) {
          const productIds = Array.from(new Set(properties.variantList.map(x => x.product_id)))
          window.pintrk('track', 'viewcategory', {
            currency: localisationData?.currency,
            country: localisationData?.country,
            event_id: properties.eventId,
            external_id: lbUserIdSHA256,
            line_items: [
              {
                product_category: properties.collectionTitle,
                product_variant_id: productIds,
              },
            ],
            ...getStorefrontIdProperty(), //storefront_id is used to identify the b2b self serve flow
          })
        }
      }, EVENT_DELAY)
    },
  },
}
